import { useMantineTheme } from "@mantine/core"
import {
  ColumnPinningState,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table"
import { CHECKBOX_COLUMN_ID } from "@tfg/ui"
import { useState } from "react"

type Props<TData> = {
  initialColumnPinning?: ColumnPinningState
} & Omit<TableOptions<TData>, "getCoreRowModel" | "getSortedRowModel">

export const useCustomReactTable = <TData>({
  initialColumnPinning,
  ...config
}: Props<TData>) => {
  const theme = useMantineTheme()
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    () => ({
      left: [CHECKBOX_COLUMN_ID, ...(initialColumnPinning?.left || [])],
    })
  )

  const initiateColumnsOrder = (columns: ColumnDef<TData>[]) =>
    columns
      .map((col: ColumnDef<TData>) => {
        const hasIdKey = typeof col === "object" && "id" in col
        const hasAccessorKey = typeof col === "object" && "accessorKey" in col

        if (hasIdKey) {
          return col.id
        }
        if (hasAccessorKey) {
          return String(col.accessorKey).split(".").join("_")
        }
        return
      })
      .filter(Boolean)

  const [orderedColumns, setOrderedColumns] = useState<string[]>(() =>
    initiateColumnsOrder(config.columns)
  )
  const [columnVisibility, setColumnVisibility] = useState({})
  const { state, ...rest } = config
  const defaultTableConfig = {
    defaultColumn: {
      size: theme.other.table.headers.size,
      minSize: theme.other.table.headers.minSize,
      maxSize: theme.other.table.headers.maxSize,
    },
    state: {
      columnPinning,
      columnVisibility,
      columnOrder: orderedColumns,
      ...state,
    },
    onColumnPinningChange: setColumnPinning,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setOrderedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  }
  return useReactTable({
    ...defaultTableConfig,
    ...rest,
  })
}
